import { useState } from 'react';
import { dismiss, notifyProblems } from '../lib/errorNotices';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_IMPORT_BYTES,
  canPostJournals,
  formatIssues,
  planStatementImport,
  readStatement,
  type StatementFormat,
} from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { FormatEditor } from '../features/statements/FormatEditor';
import { StatementPreview } from '../features/statements/StatementPreview';
import { useSession } from '../features/auth/session';
import { trpc } from '../lib/trpc';
import { useUnsavedChanges } from '../components/UnsavedChanges';

export const Route = createFileRoute('/settings/statements')({
  component: StatementFormatsPage,
});

/**
 * Bank statement formats — how each bank's CSV is read.
 *
 * THE PROBLEM THIS SCREEN SOLVES is that there is no such thing as "a bank CSV". ANZ, ASB, BNZ,
 * Westpac and Kiwibank each export a different set of columns in a different order, and each of
 * them changes it without telling anybody. An agency that cannot read its own statement cannot
 * reconcile its trust account, so this cannot be a thing they wait on a release for.
 *
 * SET UP ONCE, USED FOR EVER. The operator loads one export they already have, says which column
 * is which, and saves it under a name. From then on importing is choosing a name and a file.
 *
 * THE EXAMPLE FILE IS NEVER IMPORTED. It is here so the mapping can be checked against real
 * values while it is being made — the preview splits it into the table an import will see, with
 * each mapped column labelled, which is what turns "column 6" from a guess into a decision. It
 * never leaves the browser.
 *
 * A TRUST ROLE, not a property one. What a format says decides how a trust account's evidence is
 * read; getting the amount column wrong is how a month's statement arrives a hundred times too
 * large. Reading the list needs nothing — "which format read March" is a support question.
 */

const BLANK: StatementFormat = {
  delimiter: ',',
  hasHeader: true,
  skipRows: 0,
  dateOrder: 'dmy',
  amountSign: 'as_written',
  dateColumn: 0,
  amountColumn: 1,
  referenceColumns: [2],
  referenceSeparator: ' ',
  uniqueIdColumn: null,
  // Unmapped until the operator points at it, like the id above. Guessing a column here would
  // silently attach a wrong figure to the one number a reconciliation asks a person to confirm.
  balanceColumn: null,
};

type Draft = StatementFormat & { id: string | null; name: string };

/** One notice for the chosen file's own problems, replaced when another file is chosen. */
const READ_PROBLEM = 'import-file';
function StatementFormatsPage() {
  const form = useUnsavedChanges();
  const session = useSession();
  const mayEdit = canPostJournals(session?.roles ?? []);
  const queryClient = useQueryClient();

  const formats = useQuery(
    trpc.statementImport.formats.queryOptions(undefined, { staleTime: 30_000 }),
  );

  const [draft, setDraft] = useState<Draft | null>(null);
  const [example, setExample] = useState<{ name: string; csv: string } | null>(null);

  const done = () => {
    form.saved();
    void queryClient.invalidateQueries({ queryKey: trpc.statementImport.formats.queryKey() });
    setDraft(null);
    setExample(null);
  };

  const create = useMutation(
    trpc.statementImport.createFormat.mutationOptions({
      meta: { errorTitle: 'That format could not be saved' },
      onSuccess: done,
    }),
  );
  const update = useMutation(
    trpc.statementImport.updateFormat.mutationOptions({
      meta: { errorTitle: 'That format could not be saved' },
      onSuccess: done,
    }),
  );
  const setStatus = useMutation(
    trpc.statementImport.setFormatStatus.mutationOptions({
      meta: { errorTitle: 'Could not change whether that format is in use' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.statementImport.formats.queryKey() });
      },
    }),
  );

  /*
   * The example is read with the SAME function the server reads an import with, so the mapping
   * is checked against the file as it will actually be seen — including the delimiter and the
   * skipped rows, which change what the columns even are.
   */
  const table = example && draft ? readStatement(example.csv, draft) : null;
  const problems = draft ? formatIssues(draft, table?.columnCount) : [];
  // And planned the way an import plans it, so the preview skips, refuses and proves exactly what
  // an import of this file would.
  const plan = table && draft ? planStatementImport({ table, format: draft }) : null;

  const saving = create.isPending || update.isPending;

  if (!mayEdit && !formats.isLoading && (formats.data?.length ?? 0) === 0) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Statement formats" subtitle="How each bank’s CSV export is read" />
        <SettingsNav />
        <Panel className="p-5">
          <p className="text-[0.9375rem] font-bold">No formats yet</p>
          <p className="mt-1 text-[0.8125rem] text-ink-muted">
            Someone with a trust accounting role can set one up.
          </p>
        </Panel>
      </div>
    );
  }

  const startNew = () => {
    create.reset();
    update.reset();
    setDraft({ ...BLANK, id: null, name: '' });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={
          draft === null ? 'Statement formats' : draft.id === null ? 'New format' : 'Edit format'
        }
        subtitle="How each bank’s CSV export is read"
        actions={
          draft === null && mayEdit ? (
            <Button size="sm" onClick={startNew}>
              <span aria-hidden="true">+</span> Add format
            </Button>
          ) : null
        }
      />

      <SettingsNav />

      {draft === null ? (
        <FormatList
          formats={formats.data ?? []}
          loading={formats.isLoading}
          mayEdit={mayEdit}
          onNew={startNew}
          onEdit={(format) => {
            create.reset();
            update.reset();
            setDraft(format);
          }}
          onArchive={(id, status) => setStatus.mutate({ id, status })}
        />
      ) : (
        <div className="flex flex-col gap-4" onChange={form.touch}>
          <Panel className="p-5">
            <Field label="Name" required markOptional={false} className="max-w-xl">
              {(a) => (
                <Input
                  {...a}
                  value={draft.name}
                  maxLength={80}
                  placeholder="ANZ trust account"
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              )}
            </Field>
          </Panel>

          <StatementPreview
            file={example}
            format={draft}
            table={table}
            plan={plan}
            onChoose={(chosen) => {
              if (!chosen) return;
              dismiss(READ_PROBLEM);
              if (chosen.size > MAX_IMPORT_BYTES) {
                notifyProblems(
                  'Could not read that file',
                  [
                    'That file is larger than an import can take. Any month of it will do — this is only an example.',
                  ],
                  READ_PROBLEM,
                );
                return;
              }
              chosen
                .text()
                .then((csv) => setExample({ name: chosen.name, csv }))
                .catch(() =>
                  notifyProblems(
                    'Could not read that file',
                    ['That file could not be read. Save it again as CSV and retry.'],
                    READ_PROBLEM,
                  ),
                );
            }}
            onClear={() => setExample(null)}
          />

          <FormatEditor
            format={draft}
            onChange={(next) => {
              form.touch();
              setDraft({ ...next, id: draft.id, name: draft.name });
            }}
            headers={table?.headers ?? []}
            records={table?.records ?? []}
            issues={problems}
            plan={plan}
          />

          <div className="flex gap-2">
            <Button
              disabled={saving || problems.length > 0 || draft.name.trim() === ''}
              onClick={() => {
                // The columns are readonly on the domain type and mutable on the wire — the
                // boundary's zod array is what the router accepts.
                const { id, ...rest } = draft;
                const format = { ...rest, referenceColumns: [...rest.referenceColumns] };
                if (id === null) create.mutate(format);
                else update.mutate({ id, ...format });
              }}
            >
              {saving ? 'Saving' : draft.id === null ? 'Save format' : 'Save changes'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                form.reset();
                setDraft(null);
                setExample(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── The list ─────────────────────────────────────────────────────────────────

interface FormatRow extends StatementFormat {
  id: string;
  name: string;
  status: 'active' | 'archived';
  importCount: number;
}

function FormatList({
  formats,
  loading,
  mayEdit,
  onNew,
  onEdit,
  onArchive,
}: {
  formats: readonly FormatRow[];
  loading: boolean;
  mayEdit: boolean;
  onNew: () => void;
  onEdit: (format: Draft) => void;
  onArchive: (id: string, status: 'active' | 'archived') => void;
}) {
  return (
    <Panel>
      <PanelHeader title="Saved formats" />

      <div className="border-t border-rule">
        {loading ? (
          <p className="px-5 py-4 text-[0.8125rem] text-ink-muted">Loading…</p>
        ) : formats.length === 0 ? (
          <div className="px-5 py-6">
            <p className="text-[0.9375rem] font-bold">No formats yet</p>
            <p className="mt-1 text-[0.8125rem] text-ink-muted">
              Download a statement from your bank as CSV and map its columns once.
            </p>
            {mayEdit ? (
              <Button size="sm" className="mt-3" onClick={onNew}>
                Add format
              </Button>
            ) : null}
          </div>
        ) : (
          <ul className="divide-y divide-rule">
            {formats.map((format) => (
              <li key={format.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[0.9375rem] font-semibold">{format.name}</span>
                    {format.status === 'archived' ? <Chip tone="neutral">Archived</Chip> : null}
                  </div>
                  <p className="mt-0.5 text-[0.75rem] tabular-nums text-ink-subtle">
                    Date col {format.dateColumn + 1} · Amount col {format.amountColumn + 1} ·
                    Reference col{format.referenceColumns.length === 1 ? '' : 's'}{' '}
                    {format.referenceColumns.map((c) => c + 1).join(', ')} ·{' '}
                    {format.importCount === 0
                      ? 'never used'
                      : `${String(format.importCount)} import${format.importCount === 1 ? '' : 's'}`}
                  </p>
                </div>

                {mayEdit ? (
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onEdit({ ...format, id: format.id, name: format.name })}
                    >
                      Edit
                    </Button>
                    {/*
                     * Archived rather than deleted — every import names the format that read it,
                     * and that has to stay answerable.
                     */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        onArchive(format.id, format.status === 'active' ? 'archived' : 'active')
                      }
                    >
                      {format.status === 'active' ? 'Archive' : 'Restore'}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
